import { useEffect, useRef, useState, useCallback } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { MessageSquare, Send, Check, CheckCheck, Plus, Trash2, Save, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function CoachMessages() {
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);
  const [conv, setConv] = useState(null);
  const [input, setInput] = useState("");
  const [rules, setRules] = useState([]);
  const [savingRules, setSavingRules] = useState(false);
  const scrollRef = useRef(null);

  const loadClients = useCallback(() => api.get("/coach/premium-clients").then((r) => setClients(r.data.clients)).catch(() => {}), []);
  const loadConv = useCallback((cid) => api.get(`/coach/clients/${cid}/chat`).then((r) => { setConv(r.data); setRules(r.data.rules || []); }).catch(() => {}), []);

  useEffect(() => { loadClients(); const t = setInterval(loadClients, 8000); return () => clearInterval(t); }, [loadClients]);
  useEffect(() => {
    if (!active) return;
    loadConv(active);
    const t = setInterval(() => loadConv(active), 6000);
    return () => clearInterval(t);
  }, [active, loadConv]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [conv?.messages?.length]);

  const open = (cid) => { setActive(cid); setConv(null); loadClients(); };

  const send = async () => {
    const text = input.trim();
    if (!text || !active) return;
    setInput("");
    try {
      const { data: msg } = await api.post(`/coach/clients/${active}/chat`, { text });
      setConv((c) => ({ ...c, messages: [...(c?.messages || []), msg] }));
    } catch { toast.error("Envoi impossible"); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      await api.put(`/coach/clients/${active}/rules`, { items: rules });
      toast.success("Consignes enregistrées");
    } catch { toast.error("Erreur"); } finally { setSavingRules(false); }
  };
  const addRule = () => setRules((r) => [...r, { category: "Récupération", title: "", text: "" }]);
  const updRule = (i, k, v) => setRules((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const delRule = (i) => setRules((r) => r.filter((_, j) => j !== i));

  return (
    <CoachLayout>
      <div className="fade-up">
        <h1 className="text-2xl font-medium flex items-center gap-2"><MessageSquare size={20} strokeWidth={1.6} /> Messagerie Premium</h1>
        <p className="mt-2 text-sm text-obsidian/50">Échange en direct avec tes clients Premium et fixe leurs consignes personnalisées.</p>

        <div className="mt-6 grid lg:grid-cols-[300px_1fr] gap-5">
          {/* Liste clients */}
          <div className="border border-border bg-white rounded-xl overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="premium-client-list">
            {clients.length === 0 && <div className="p-5 text-sm text-obsidian/50">Aucun client Premium pour l'instant.</div>}
            {clients.map((c) => (
              <button key={c.user_id} data-testid={`pm-client-${c.user_id}`} onClick={() => open(c.user_id)}
                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 flex items-center gap-3 transition-[background-color] ${active === c.user_id ? "bg-secondary" : "hover:bg-secondary/50"}`}>
                {c.picture ? <img src={c.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">{(c.name || "?").charAt(0)}</span>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-2">{c.name}
                    {c.unread > 0 && <span data-testid={`pm-unread-${c.user_id}`} className="text-[10px] bg-sage text-white rounded-full px-1.5 py-0.5">{c.unread}</span>}
                  </div>
                  <div className="text-xs text-obsidian/50 truncate">{c.last_message || "Nouvelle conversation"}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Conversation + consignes */}
          <div>
            {!active && <div className="border border-dashed border-border rounded-xl p-12 text-center text-sm text-obsidian/50">Sélectionne un client pour ouvrir la conversation.</div>}
            {active && conv && (
              <div className="space-y-5">
                <div className="border border-border bg-white rounded-xl flex flex-col h-[52vh]" data-testid="pm-conversation">
                  <div className="px-4 py-3 border-b border-border font-medium">{conv.client?.name}</div>
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                    {conv.messages.length === 0 && <div className="text-center text-sm text-obsidian/40 mt-6">Aucun message. Démarre la conversation.</div>}
                    {conv.messages.map((m) => (
                      <div key={m.message_id} className={`flex ${m.sender === "coach" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.sender === "coach" ? "bg-obsidian text-white rounded-tr-sm" : "bg-secondary rounded-tl-sm"}`}>
                          <div>{m.text}</div>
                          {m.sender === "coach" && <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-white/60">{m.read_at ? <><CheckCheck size={12} /> Lu</> : <><Check size={12} /> Envoyé</>}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-end gap-2 p-3 border-t border-border">
                    <textarea data-testid="pm-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Répondre…" className="flex-1 resize-none rounded-lg border border-border px-3 py-2 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
                    <button data-testid="pm-send" onClick={send} disabled={!input.trim()} className="w-10 h-10 shrink-0 rounded-full bg-obsidian text-white flex items-center justify-center disabled:opacity-30"><Send size={16} /></button>
                  </div>
                </div>

                {/* Éditeur de consignes */}
                <div className="border border-border bg-white rounded-xl p-4" data-testid="pm-rules-editor">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium"><ClipboardList size={16} className="text-sage" /> Consignes visibles par le client</div>
                    <button data-testid="pm-rules-add" onClick={addRule} className="text-xs inline-flex items-center gap-1 text-sage hover:underline"><Plus size={14} /> Ajouter</button>
                  </div>
                  <div className="mt-3 space-y-3">
                    {rules.length === 0 && <div className="text-xs text-obsidian/50">Aucune consigne. Ajoute-en une (récupération, nutrition, sommeil…).</div>}
                    {rules.map((r, i) => (
                      <div key={i} data-testid={`pm-rule-${i}`} className="grid sm:grid-cols-[140px_1fr_auto] gap-2 items-start">
                        <input value={r.category || ""} onChange={(e) => updRule(i, "category", e.target.value)} placeholder="Catégorie" className="rounded-md border border-border px-2.5 py-2 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
                        <div className="space-y-2">
                          <input value={r.title || ""} onChange={(e) => updRule(i, "title", e.target.value)} placeholder="Titre (ex : Sommeil)" className="w-full rounded-md border border-border px-2.5 py-2 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
                          <textarea value={r.text || ""} onChange={(e) => updRule(i, "text", e.target.value)} rows={2} placeholder="Consigne détaillée…" className="w-full rounded-md border border-border px-2.5 py-2 text-sm focus:ring-1 focus:ring-sage focus:outline-none" />
                        </div>
                        <button data-testid={`pm-rule-del-${i}`} onClick={() => delRule(i)} className="p-2 text-destructive hover:bg-destructive/10 rounded-md"><Trash2 size={15} /></button>
                      </div>
                    ))}
                  </div>
                  <button data-testid="pm-rules-save" onClick={saveRules} disabled={savingRules} className="mt-4 rounded-full bg-obsidian text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"><Save size={15} /> Enregistrer les consignes</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}
